import { IconNames } from '@blueprintjs/icons';
import { ActionButtonText } from '@strata/react-buttons';
import { FormInputContainer } from '@strata/react-form-input-container';
import { TextInputFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { Icon } from '@strata/react-status-icons';
import { validationStrings } from '@strata/strata-ui-strings';
import { FieldArray, FieldArrayRenderProps, useFormikContext } from 'formik';
import { ComponentProps } from 'react';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { PatientCriteria } from 'components/admin/automations/templateSections/criteriaComponents/PatientCriteria';
import {
    AutomationRuleCriteria,
    AutomationRuleFormContext,
    PerformanceCriteria,
} from 'services/AutomationRuleHelpers';

const CriteriaComponentMap = {
    [AutomationRuleCriteria.PATIENT]: PatientCriteria,
};

interface CriteriaSectionProps {
    criteriaPropsMap: Partial<
        Record<
            (typeof AutomationRuleCriteria)[keyof typeof AutomationRuleCriteria],
            {
                values: ComponentProps<typeof SuggestSingleSelectFormik>['values'];
                onChange?: (
                    newValue:
                        | Required<ComponentProps<typeof SuggestSingleSelectFormik>>['values'][0]
                        | undefined,
                    arrayHelpers: FieldArrayRenderProps,
                ) => Promise<unknown>;
                disabled?: boolean;
            }
        >
    >;
    additionalCriteriaOptions?: (typeof AutomationRuleCriteria)[keyof typeof AutomationRuleCriteria][];
    userCanEdit: boolean;
}

export const CriteriaSection = ({
    criteriaPropsMap,
    additionalCriteriaOptions = undefined,
    userCanEdit,
}: CriteriaSectionProps) => {
    const formik = useFormikContext<AutomationRuleFormContext>();
    const isCriterionOptional = (
        criterion: (typeof AutomationRuleCriteria)[keyof typeof AutomationRuleCriteria],
    ) => criterion === undefined || additionalCriteriaOptions?.includes(criterion);
    return (
        <FormInputContainer>
            <FieldArray name="criteria">
                {(arrayHelpers) => {
                    const criteria = formik.values[arrayHelpers.name as 'criteria'];
                    return (
                        <>
                            {criteria.map(({ criterion, tempId }, index) => {
                                const { disabled, values, onChange } =
                                    criteriaPropsMap[criterion] ?? {};
                                const CriteriaComponent =
                                    CriteriaComponentMap[
                                        criterion as keyof typeof CriteriaComponentMap
                                    ];
                                const criterionOptional = isCriterionOptional(criterion);
                                return (
                                    <div
                                        key={criterion ?? tempId}
                                        className="sds_automation-template__row"
                                    >
                                        <div className="sds_automation-template__label sds_label font-bold--uppercase">
                                            {index === 0 ? 'When' : 'And'}
                                        </div>
                                        {criterionOptional ? (
                                            <SuggestSingleSelectFormik
                                                id={`${arrayHelpers.name}.${index}.criterion`}
                                                values={additionalCriteriaOptions?.filter(
                                                    (option) =>
                                                        option === criterion ||
                                                        !criteria
                                                            ?.map((c) => c.criterion)
                                                            .includes(option),
                                                )}
                                                required
                                                verticalSpacingSmall
                                                onChange={(newValue) =>
                                                    arrayHelpers.replace(index, {
                                                        criterion: newValue,
                                                    })
                                                }
                                            />
                                        ) : (
                                            <TextInputFormik
                                                id={`${arrayHelpers.name}.${index}.criterion`}
                                                value={criterion}
                                                required
                                                verticalSpacingSmall
                                                disabled
                                            />
                                        )}
                                        <div className="sds_automation-template__label">is</div>
                                        {CriteriaComponent ? (
                                            <CriteriaComponent
                                                arrayHelpers={arrayHelpers}
                                                index={index}
                                                disabled={!userCanEdit || !!disabled}
                                            />
                                        ) : (
                                            <SuggestSingleSelectFormik
                                                id={`${arrayHelpers.name}.${index}.value`}
                                                placeholderText={
                                                    criterion ? `Select ${criterion}` : ''
                                                }
                                                disabled={!userCanEdit || !!disabled}
                                                values={values}
                                                isLoading={!values}
                                                required
                                                verticalSpacingSmall
                                                onChange={(newValue) => {
                                                    (async () => {
                                                        await formik.setFieldValue(
                                                            `${arrayHelpers.name}.${index}.value`,
                                                            newValue,
                                                        );
                                                        await onChange?.(newValue, arrayHelpers);
                                                    })();
                                                }}
                                            />
                                        )}
                                        {criterionOptional && (
                                            <Icon
                                                className="margin__top--small"
                                                icon={IconNames.CROSS}
                                                title="Remove"
                                                onClick={() => arrayHelpers.remove(index)}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                            {criteria.filter(({ criterion }) => isCriterionOptional(criterion))
                                .length < (additionalCriteriaOptions ?? []).length && (
                                <div className="sds_automation-template__row">
                                    <div style={{ gridColumnStart: 2, gridColumnEnd: 5 }}>
                                        <ActionButtonText
                                            leftIcon={IconNames.ADD}
                                            label="Add Another Condition"
                                            disabled={!userCanEdit}
                                            // Store temp id for array keys
                                            onClick={() => arrayHelpers.push({ tempId: uuidv4() })}
                                        />
                                    </div>
                                </div>
                            )}
                        </>
                    );
                }}
            </FieldArray>
        </FormInputContainer>
    );
};

CriteriaSection.validationSchema = {
    criteria: Yup.array().of(
        Yup.object({
            criterion: Yup.string().required(validationStrings.requiredField.errorMessage),
            value: Yup.object()
                .required(validationStrings.requiredField.errorMessage)
                .when(
                    'criterion',
                    ([criterion]: (keyof typeof CriteriaComponentMap)[], schema) =>
                        CriteriaComponentMap[criterion]?.validationSchema?.(schema) ?? schema,
                ),
        }),
    ),
};

CriteriaSection.submitValues = (values: AutomationRuleFormContext) => {
    let submitValues: {
        segmentId?: string;
        payerId?: string;
        productServiceOid?: string;
        isBrand?: boolean;
        performance?: (typeof PerformanceCriteria)[keyof typeof PerformanceCriteria];
        placeOfService?: string;
        prescriberListId?: string;
        stateListId?: string;
    } = {};
    values.criteria?.forEach(({ criterion, value }) => {
        if (criterion === AutomationRuleCriteria.PAYER) {
            submitValues.segmentId = value?.id;
            submitValues.payerId = value?.payerId;
        } else if (criterion === AutomationRuleCriteria.PRODUCT) {
            submitValues.productServiceOid = value?.oid;
            submitValues.isBrand = value?.isBrand;
        } else if (criterion === AutomationRuleCriteria.PERFORMANCE) {
            submitValues.performance = value?.id;
        } else if (criterion === AutomationRuleCriteria.PLACE_OF_SERVICE) {
            submitValues.placeOfService = value?.id;
        } else if (criterion === AutomationRuleCriteria.PRESCRIBER_LIST) {
            submitValues.prescriberListId = value?.prescriberListId;
        } else if (criterion === AutomationRuleCriteria.STATE_LIST) {
            submitValues.stateListId = value?.stateListId;
        } else {
            submitValues = {
                ...submitValues,
                ...CriteriaComponentMap[
                    criterion as keyof typeof CriteriaComponentMap
                ]?.submitValues?.(value),
            };
        }
    });
    return submitValues;
};

CriteriaSection.duplicateValues = (values: AutomationRuleFormContext) =>
    CriteriaSection.submitValues(values);
